import { tracker } from '../../utils/core';
import type { ReceiptTransactionMatch } from './receiptMatch';
import { isLocalReceipt, isProcessingErrorReceipt } from './receiptsPredicates';
import type { Receipt, ReceiptFeedbackReason, ReceiptStatus, ReceiptSubStatus } from './receiptsTypes';

export type ReceiptCaptureMethod = 'scan' | 'import';
export type ReceiptCaptureSource =
  | 'mobile-camera'
  | 'mobile-photo-library'
  | 'mobile-files'
  | 'web-file-picker'
  | 'web-drag-drop';
export type ReceiptExperienceType = 'first-time' | 'ongoing';
export type ReceiptUploadFlow = 'confirm-before-upload' | 'immediate';
export type ReceiptFailureType = 'upload' | 'extraction' | 'link';
export type ReceiptState = 'needs-review' | 'reviewed' | 'processing' | 'failed';
/** Where a receipt flow ran, so transaction-details uploads can be compared with Receipt Center. */
export type ReceiptOriginLocation = 'transaction-detail' | 'receipt-center';
/** Unknown when the client did not see the upload start, such as after a restart or from another device. */
export type ReceiptProcessingLocation = ReceiptOriginLocation | 'unknown';
export type ReceiptDetachLocation = ReceiptOriginLocation;
export type ReceiptDetailsViewLocation =
  | 'transaction-detail'
  | 'receipt-center'
  | 'attach-existing'
  | 'create-new-transaction';
export type ReceiptFeedbackLocation = ReceiptOriginLocation;
/** How the suggested transaction's amount lined up with the receipt total. */
export type ReceiptSuggestionAmountMatch = ReceiptTransactionMatch['amountMatch'];
/** Why a linkable receipt showed no suggestion: no extracted total to search on, or no transaction matched. */
export type ReceiptSuggestionMissReason = 'no-total' | 'no-match';
/**
 * Analytics context for attaching to a transaction that already exists, carried through to the result event.
 * `attach-suggested` is the one-tap attach from the receipt detail suggestion; `attach-existing` is the picker.
 */
export type ReceiptAttachAnalytics =
  | { actionType: 'attach-existing' }
  | { actionType: 'attach-suggested'; amountMatch: ReceiptSuggestionAmountMatch };
/** Analytics context for any action that ends with the receipt linked to a transaction. */
export type ReceiptLinkAnalytics = ReceiptAttachAnalytics | { actionType: 'create-new' };
export type ReceiptProcessingStage = 'upload' | 'extraction';
export type ReceiptProcessingFailureReason =
  | 'upload-failed'
  | 'upload-timeout'
  | 'upload-expired'
  | 'malware-detected'
  | 'quota-exceeded'
  | 'storage-not-ready'
  | 'creation-failed'
  | 'creation-timeout'
  | 'extraction-failed'
  | 'extraction-timeout'
  | 'not-a-receipt'
  | 'unsupported-format'
  | 'unreadable'
  | 'unknown';

type ReceiptFeedbackAnalyticsContext = {
  receiptId: string;
  location: ReceiptFeedbackLocation;
  receiptStatus: ReceiptStatus;
  receiptState: ReceiptState;
  extractionEngine?: string;
  isAttached: boolean;
};

export type ReceiptAnalyticsEvent =
  | { event: 'receiptCenterOpen'; properties: { location: 'main-navigation' } }
  | { event: 'receiptTransactionUploadStart'; properties: { location: 'transaction-detail' } }
  | {
      event: 'receiptCaptureStart';
      properties: {
        captureMethod: ReceiptCaptureMethod;
        captureSource: ReceiptCaptureSource;
        /** Receipts staged by this capture gesture. Absent where a gesture can only yield one. */
        fileCount?: number;
      } & (
        | { location: 'receipt-center'; experienceType: ReceiptExperienceType }
        // Mobile transaction details has no intro state; web reports its shared staging hook's value.
        | { location: 'transaction-detail'; experienceType?: ReceiptExperienceType }
      );
    }
  | {
      event: 'receiptCaptureCancelled';
      properties: {
        location: 'transaction-detail';
        captureMethod: ReceiptCaptureMethod;
        captureSource: ReceiptCaptureSource;
      };
    }
  | {
      event: 'receiptUploadStaged';
      properties: {
        captureSource: ReceiptCaptureSource;
        experienceType: ReceiptExperienceType;
        fileCount: number;
        stagedFileCount: number;
      };
    }
  | {
      event: 'receiptUploadCancelled' | 'receiptUploadConfirmed';
      properties: { experienceType: ReceiptExperienceType; fileCount: number };
    }
  | {
      event: 'receiptUploadSubmitted';
      properties: {
        experienceType: ReceiptExperienceType;
        fileCount: number;
        flow: ReceiptUploadFlow;
        location: ReceiptOriginLocation;
      };
    }
  | { event: 'receiptListView'; properties: { viewType: 'needs-review' | 'reviewed' } }
  | {
      event: 'receiptProcessingResult';
      properties: { location: ReceiptProcessingLocation } & (
        | { stage: ReceiptProcessingStage; result: 'success' }
        | { stage: ReceiptProcessingStage; result: 'failed'; reason: ReceiptProcessingFailureReason }
      );
    }
  | {
      event: 'receiptTransactionLinkResult';
      properties: { location: 'transaction-detail'; result: 'success' | 'failed' };
    }
  | {
      event: 'receiptFailureAction';
      properties: { failureType: ReceiptFailureType; actionType: 'retry' | 'delete'; location: ReceiptOriginLocation };
    }
  | { event: 'receiptDelete'; properties: { receiptState: ReceiptState } }
  | {
      event: 'receiptAction';
      properties:
        | { actionType: 'delete' }
        /** `hasSuggestion` is set only when started from receipt details, where a suggestion may be on screen. */
        | { actionType: 'create-new' | 'attach-existing'; hasSuggestion?: boolean }
        | { actionType: 'attach-suggested'; amountMatch: ReceiptSuggestionAmountMatch };
    }
  | {
      event: 'receiptActionResult';
      properties:
        | { actionType: 'create-new' | 'attach-existing'; result: 'success' | 'failed' }
        /** `stale`: the suggestion changed before the tap was handled, so nothing was linked. */
        | {
            actionType: 'attach-suggested';
            amountMatch: ReceiptSuggestionAmountMatch;
            result: 'success' | 'failed' | 'stale';
          };
    }
  /**
   * Once per linkable receipt opened: `hasSuggestion: true` when a suggestion is first shown, otherwise
   * `hasSuggestion: false` with the reason when the receipt closes without one (coverage).
   */
  | {
      event: 'receiptSuggestionView';
      properties:
        | { hasSuggestion: true; amountMatch: ReceiptSuggestionAmountMatch }
        | { hasSuggestion: false; reason: ReceiptSuggestionMissReason };
    }
  | {
      event: 'receiptCreateSplits';
      properties: { location: 'create-new-transaction' | 'existing-transaction' };
    }
  | {
      event: 'receiptDetailsView';
      properties: { location: ReceiptDetailsViewLocation };
    }
  | { event: 'receiptDetach'; properties: { location: ReceiptDetachLocation } }
  | {
      event: 'receiptFeedbackPositive';
      properties: ReceiptFeedbackAnalyticsContext & { feedbackStage: 'rating' };
    }
  | {
      event: 'receiptFeedbackNegative';
      properties:
        | (ReceiptFeedbackAnalyticsContext & {
            feedbackStage: 'rating';
            failureReasons: [];
          })
        | (ReceiptFeedbackAnalyticsContext & {
            feedbackStage: 'details';
            failureReasons: ReceiptFeedbackReason[];
            failureComment?: string;
            reasonCount: number;
            hasComment: boolean;
          });
    };

export const trackReceiptEvent = ({ event, properties }: ReceiptAnalyticsEvent): void => {
  tracker.track(tracker.events[event], properties);
};

type ReceiptFailureMetadata = {
  failureType: ReceiptFailureType;
  reason: ReceiptProcessingFailureReason;
};

const FAILURE_METADATA: Partial<Record<ReceiptSubStatus, ReceiptFailureMetadata>> = {
  UPLOAD_FAILED: { failureType: 'upload', reason: 'upload-failed' },
  UPLOAD_EXPIRED: { failureType: 'upload', reason: 'upload-expired' },
  MALWARE_DETECTED: { failureType: 'upload', reason: 'malware-detected' },
  QUOTA_EXCEEDED: { failureType: 'upload', reason: 'quota-exceeded' },
  STORAGE_NOT_READY: { failureType: 'upload', reason: 'storage-not-ready' },
  CREATION_FAILED: { failureType: 'upload', reason: 'creation-failed' },
  EXTRACTION_FAILED: { failureType: 'extraction', reason: 'extraction-failed' },
  NOT_A_RECEIPT: { failureType: 'extraction', reason: 'not-a-receipt' },
  UNSUPPORTED_FORMAT: { failureType: 'extraction', reason: 'unsupported-format' },
  UNREADABLE: { failureType: 'extraction', reason: 'unreadable' },
};

export const getReceiptFailureType = (
  receipt: Pick<Receipt, 'id' | 'status' | 'subStatus' | 'targetTransactionId' | 'linkFailed'>,
): ReceiptFailureType => {
  if (isLocalReceipt(receipt)) return 'upload';
  if (receipt.status === 'READY' && receipt.targetTransactionId && receipt.linkFailed) return 'link';
  return receipt.subStatus ? (FAILURE_METADATA[receipt.subStatus]?.failureType ?? 'extraction') : 'extraction';
};

export const getReceiptState = (receipt: Pick<Receipt, 'status' | 'subStatus' | 'transactionId'>): ReceiptState => {
  if (receipt.transactionId) return 'reviewed';
  if (isProcessingErrorReceipt(receipt)) return 'failed';
  if (receipt.status !== 'READY') return 'processing';
  return 'needs-review';
};

export type ReceiptProcessingTransition = Extract<
  ReceiptAnalyticsEvent,
  { event: 'receiptProcessingResult' }
>['properties'];

/**
 * @param originsByKey Where each upload this session started, by receipt id and document id. A receipt
 * without one, such as one restored after a restart or uploaded elsewhere, reports an unknown location.
 */
export const getReceiptProcessingTransitions = (
  previousReceipts: ReadonlyArray<Receipt>,
  currentReceipts: ReadonlyArray<Receipt>,
  didExpireStaleReceipts: boolean,
  originsByKey: ReadonlyMap<string, ReceiptOriginLocation> = new Map(),
): ReceiptProcessingTransition[] => {
  const currentByDocumentId = new Map(currentReceipts.map((receipt) => [receipt.documentId, receipt]));
  const transitions: ReceiptProcessingTransition[] = [];

  previousReceipts.forEach((previous) => {
    const current = currentByDocumentId.get(previous.documentId);
    if (!current || current.isDeleted) return;

    const location: ReceiptProcessingLocation =
      previous.targetTransactionId || current.targetTransactionId
        ? 'transaction-detail'
        : ([previous.id, current.id, current.documentId]
            .map((key) => originsByKey.get(key))
            .find((origin) => origin !== undefined) ?? 'unknown');

    const wasLocal = isLocalReceipt(previous);
    const isCurrentLocal = isLocalReceipt(current);
    const currentFailed = isProcessingErrorReceipt(current);
    const uploadAccepted = wasLocal && !isCurrentLocal;
    const currentFailureType = currentFailed ? getReceiptFailureType(current) : undefined;

    if (previous.status === current.status && !uploadAccepted) return;

    if (uploadAccepted && currentFailureType !== 'upload') {
      transitions.push({ location, stage: 'upload', result: 'success' });
    } else if (wasLocal && currentFailed && currentFailureType === 'upload') {
      transitions.push({
        location,
        stage: 'upload',
        result: 'failed',
        reason: didExpireStaleReceipts
          ? previous.status === 'UPLOAD_PENDING'
            ? 'upload-timeout'
            : 'creation-timeout'
          : (FAILURE_METADATA[current.subStatus ?? 'UNKNOWN']?.reason ?? 'unknown'),
      });
    }

    const wasExtracting = !wasLocal && (previous.status === 'UPLOADED' || previous.status === 'SCANNING');
    if ((wasExtracting || uploadAccepted) && current.status === 'READY') {
      transitions.push({ location, stage: 'extraction', result: 'success' });
    } else if ((wasExtracting || uploadAccepted) && currentFailed && currentFailureType === 'extraction') {
      transitions.push({
        location,
        stage: 'extraction',
        result: 'failed',
        reason: didExpireStaleReceipts
          ? 'extraction-timeout'
          : (FAILURE_METADATA[current.subStatus ?? 'UNKNOWN']?.reason ?? 'unknown'),
      });
    }
  });

  return transitions;
};
